/**
 * FlagToggles — reusable tri-state segmented control for
 * precache / prelaunch / multiplex flags. Each button cycles:
 *
 *   inherit (null) → on (true) → off (false) → inherit (null) → …
 *
 * Inherited values display the resolved pool default beneath the label
 * so operators can see what "inherit" means without a trip to the header.
 * Prelaunch is disabled when effective precache is off, matching the
 * resolver clamp (`prelaunch && !precache ⇒ precache = true`).
 *
 * Reused by InclusionCard (inclusion-scope flags) and by override-action
 * exception chips (refinement-scope flags) — one component, no copies.
 */

import type { PoolFlagDefaults } from '@interlucent/organization-protocol';
import styles from './FlagToggles.module.css';
import { WarmTargetInput } from './WarmTargetInput';

type BoolFlagName = 'precache' | 'multiplex';

export interface FlagTogglesValue {
  precache: boolean | null;
  /** @deprecated Use `warmTarget`. Kept transitionally for callers that haven't migrated. */
  prelaunch?: boolean | null;
  warmTarget: number | null;
  multiplex: boolean | null;
}

export interface FlagTogglesChange {
  precache?: boolean | null;
  /** @deprecated Use `warmTarget`. */
  prelaunch?: boolean | null;
  warmTarget?: number | null;
  multiplex?: boolean | null;
}

export interface FlagTogglesProps {
  value: FlagTogglesValue;
  poolDefaults: PoolFlagDefaults;
  onChange: (next: FlagTogglesChange) => void;
  disabled?: boolean;
  /** Compact mode for exception chips — tighter padding. */
  compact?: boolean;
}

function nextState(current: boolean | null): boolean | null {
  if (current === null) return true;
  if (current === true) return false;
  return null;
}

export function FlagToggles({ value, poolDefaults, onChange, disabled }: FlagTogglesProps) {
  const cycle = (name: BoolFlagName) => {
    onChange({ [name]: nextState(value[name]) } as FlagTogglesChange);
  };

  return (
    <div className={styles.cluster} role="group" aria-label="Flag overrides">
      <ToggleButton
        label="precache"
        override={value.precache}
        poolDefault={poolDefaults.precache}
        disabled={disabled}
        onClick={() => cycle('precache')}
      />
      <WarmTargetInput
        value={value.warmTarget}
        poolDefault={poolDefaults.warmTarget}
        disabled={disabled}
        onChange={(next) => onChange({ warmTarget: next })}
      />
      <ToggleButton
        label="multiplex"
        override={value.multiplex}
        poolDefault={poolDefaults.multiplex}
        disabled={disabled}
        onClick={() => cycle('multiplex')}
      />
    </div>
  );
}

function ToggleButton({
  label,
  override,
  poolDefault,
  disabled,
  disabledReason,
  onClick,
}: {
  label: string;
  override: boolean | null;
  poolDefault: boolean;
  disabled?: boolean;
  disabledReason?: string;
  onClick: () => void;
}) {
  const explicit = override !== null;
  const on = explicit ? override === true : poolDefault;
  const cls = [
    styles.toggle,
    explicit ? (on ? styles.toggleOn : styles.toggleOff) : styles.toggleInh,
  ].join(' ');

  const title = disabledReason
    ? disabledReason
    : explicit
    ? `Explicit: ${on ? 'on' : 'off'} · click to cycle to ${on ? 'off' : 'inherit'}`
    : `Inherited: pool default is ${poolDefault ? 'on' : 'off'} · click to set explicit on`;

  const stateLabel = explicit ? (on ? 'on' : 'off') : `inh (${poolDefault ? 'on' : 'off'})`;

  return (
    <button type="button" className={cls} onClick={onClick} disabled={disabled} title={title}>
      <span className={styles.label}>{label}</span>
      <span className={styles.state}>{stateLabel}</span>
    </button>
  );
}
